'use client';

import { useMemo, useState, type ReactElement } from 'react';
import { useFormWithBlurValidation } from '@/hooks/use-form-with-blur-validation';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { createGiftCardCheckBalanceFormSchema, type GiftCardFormData } from '@/lib/checkout-schemas';
import { formatCurrency } from '@/lib/currency';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { CustomInput } from '@/components/ui/custom-input';
import { Form, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { RequiredFieldsLegend } from '@/components/required-fields-legend';
import { Spinner } from '@/components/spinner';
import { SvgRight } from '@/components/icons-eataly';
import { TurnstileWidget } from '@/components/security/turnstile-widget';
import { useTurnstile } from '@/hooks/use-turnstile';

type GiftCardBalanceResult = {
    status: string;
    amount?: number;
    currency?: string;
};

function resolveBalanceError(status: string | undefined, tProduct: TFunction<'product'>, tCheckout: TFunction) {
    switch (status) {
        case 'INVALID_CARD':
        case 'EXPIRED':
            return tProduct('checkBalance.errors.invalidCard', {
                defaultValue: 'The code entered is not valid.',
            });
        case 'INSUFFICIENT_FUNDS':
            // For “credit residuo”, insufficient funds means “no available balance”.
            return tCheckout('payment.giftCard.noBalance', 'This gift card has no available balance.');
        case 'PROCESSOR_ERROR':
        case 'DECLINED':
        case 'PARTIAL':
        default:
            return tProduct('checkBalance.errors.generic', {
                defaultValue: 'Unable to verify the gift card code. Please try again.',
            });
    }
}

export default function GiftCardCheckBalance(): ReactElement {
    const { t: tProduct, i18n } = useTranslation('product');
    const { t: tCheckout } = useTranslation('checkout');
    const turnstile = useTurnstile('giftcard-balance');

    const schema = useMemo(() => createGiftCardCheckBalanceFormSchema(tProduct as never), [tProduct]);

    const form = useFormWithBlurValidation<GiftCardFormData>({
        resolver: zodResolver(schema),
        mode: 'onBlur',
        defaultValues: {
            cardNumber: '',
        },
    });

    const [isBusy, setIsBusy] = useState(false);
    const [balance, setBalance] = useState<{ amount: number; currency?: string } | null>(null);

    const webCodeLabel = tProduct('checkBalance.webCodeLabel', { defaultValue: 'Codice web' });
    const verifyCta = tProduct('checkBalance.verifyCta', { defaultValue: 'VERIFICA' });
    const verifyingLabel = tProduct('checkBalance.verifying', { defaultValue: 'Verifica in corso…' });

    const onSubmit = async (data: GiftCardFormData) => {
        if (turnstile.isPending) {
            return;
        }

        setIsBusy(true);
        setBalance(null);
        form.clearErrors('cardNumber');

        try {
            const response = await fetch('/resource/giftcard/balance', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    cardNumber: data.cardNumber.trim(),
                    ...(turnstile.token ? { turnstileToken: turnstile.token } : {}),
                }),
            });
            const result = (await response.json()) as GiftCardBalanceResult;

            if (result?.status === 'OK' && typeof result.amount === 'number') {
                setBalance({ amount: result.amount, currency: result.currency });
                form.clearErrors('cardNumber');
                return;
            }

            const message = resolveBalanceError(result?.status, tProduct, tCheckout as never);
            form.setError('cardNumber', { type: 'manual', message });
        } catch {
            const message = resolveBalanceError(undefined, tProduct, tCheckout as never);
            form.setError('cardNumber', { type: 'manual', message });
        } finally {
            setIsBusy(false);
            if (turnstile.enabled) {
                turnstile.reset();
            }
        }
    };

    const handleFormSubmit = form.handleSubmit(onSubmit);

    return (
        <section className="py-12 col-span-full lg:col-span-10 lg:col-start-2">
            <div className="mx-auto max-w-screen-2xl grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-10">
                <div className="flex flex-col gap-4">
                    <h2 className="heading-headline-m text-foreground">
                        {tProduct('checkBalance.sectionTitle', { defaultValue: 'Hai già una carta regalo?' })}
                    </h2>
                    <p className="body-copy-l text-foreground">
                        {tProduct('checkBalance.sectionDescription', {
                            defaultValue: 'Verifica il credito residuo della tua Eataly Card.',
                        })}
                    </p>
                </div>

                <div className={cn('bg-neutral-white rounded-lg p-6 flex flex-col gap-6', 'shadow-none')}>
                    <div className="flex flex-col gap-2">
                        <h3 className="heading-title-m text-neutral-10">
                            {tProduct('checkBalance.cardTitle', { defaultValue: 'Verifica il credito' })}
                        </h3>
                        <p className="body-copy-l text-neutral-10">
                            {tProduct('checkBalance.cardDescription', {
                                defaultValue:
                                    'Per verificare il credito residuo della tua Card fisica o virtuale, inserisci qui sotto il codice web e clicca su "verifica".',
                            })}
                        </p>
                    </div>

                    <Form {...form}>
                        <form
                            onSubmit={(event) => void handleFormSubmit(event)}
                            className="flex flex-col gap-6"
                            noValidate>
                            <RequiredFieldsLegend />
                            <FormField
                                control={form.control}
                                name="cardNumber"
                                render={({ field, fieldState }) => (
                                    <FormItem>
                                        <FormLabel className="sr-only">{webCodeLabel}</FormLabel>
                                        <CustomInput
                                            id="gift-card-web-code"
                                            label={webCodeLabel}
                                            placeholder={webCodeLabel}
                                            errorId="gift-card-web-code-error"
                                            hintId="gift-card-web-code-hint"
                                            hintText={webCodeLabel}
                                            errorMessage={fieldState.error?.message}
                                            inputClassName="rounded-sm"
                                            autoComplete="off"
                                            inputMode="text"
                                            disabled={isBusy}
                                            required
                                            {...field}
                                            onChange={(event) => {
                                                field.onChange(event);
                                                setBalance(null);
                                                if (fieldState.error?.type === 'manual') {
                                                    form.clearErrors('cardNumber');
                                                }
                                            }}
                                        />
                                    </FormItem>
                                )}
                            />

                            {turnstile.enabled && turnstile.siteKey ? (
                                <TurnstileWidget
                                    siteKey={turnstile.siteKey}
                                    action={turnstile.action}
                                    onSuccess={turnstile.handleSuccess}
                                    onError={turnstile.handleError}
                                    onExpire={turnstile.handleExpire}
                                    enabled={turnstile.enabled}
                                    mode={turnstile.mode}
                                    resetRef={turnstile.resetRef}
                                />
                            ) : null}

                            <div className="flex flex-col gap-3">
                                <Button
                                    type="submit"
                                    hierarchy="primary"
                                    size="large"
                                    className="w-full rounded-full"
                                    disabled={isBusy || turnstile.isPending}
                                    aria-busy={isBusy}
                                    iconAfter={<SvgRight className="size-5 shrink-0" aria-hidden />}>
                                    {isBusy ? (
                                        <>
                                            <Spinner size="sm" />
                                            {verifyingLabel}
                                        </>
                                    ) : (
                                        verifyCta
                                    )}
                                </Button>
                            </div>
                        </form>
                    </Form>

                    {balance ? (
                        <p className="body-copy-l text-neutral-10" aria-live="polite" data-testid="gift-card-balance">
                            {tProduct('checkBalance.remainingBalance', {
                                defaultValue: 'Credito residuo: {{amount}}',
                                amount: formatCurrency(balance.amount, i18n.language, balance.currency ?? 'EUR'),
                            })}
                        </p>
                    ) : null}
                </div>
            </div>
        </section>
    );
}
